import React, { createContext, useContext, useState, useCallback } from "react";

const LoadingContext = createContext({
  loadingState: {
    fullPage: false,
    masked: false,
    partial: {},
  },
  showFullPageLoading: () => {},
  hideFullPageLoading: () => {},
  showMaskedLoading: () => {},
  hideMaskedLoading: () => {},
  showPartialLoading: (containerId) => {},
  hidePartialLoading: (containerId) => {},
  hideAllLoading: () => {},
});

export const LoadingProvider = ({ children }) => {
  const [loadingState, setLoadingState] = useState({
    fullPage: false,
    masked: false,
    partial: {}, // { containerId: true/false }
  });

  const showFullPageLoading = useCallback(() => {
    setLoadingState((prev) => ({ ...prev, fullPage: true }));
  }, []);

  const hideFullPageLoading = useCallback(() => {
    setLoadingState((prev) => ({ ...prev, fullPage: false }));
  }, []);

  const showMaskedLoading = useCallback(() => {
    setLoadingState((prev) => ({ ...prev, masked: true }));
  }, []);

  const hideMaskedLoading = useCallback(() => {
    setLoadingState((prev) => ({ ...prev, masked: false }));
  }, []);

  const showPartialLoading = useCallback((containerId) => {
    setLoadingState((prev) => ({
      ...prev,
      partial: { ...prev.partial, [containerId]: true },
    }));
  }, []);

  const hidePartialLoading = useCallback((containerId) => {
    setLoadingState((prev) => {
      const newPartial = { ...prev.partial };
      delete newPartial[containerId];
      return { ...prev, partial: newPartial };
    });
  }, []);

  const hideAllLoading = useCallback(() => {
    setLoadingState({
      fullPage: false,
      masked: false,
      partial: {},
    });
  }, []);

  // Legacy support for existing showLoading/hideLoading calls
  const showLoading = useCallback(
    (opacity) => {
      if (typeof opacity === "number") {
        showMaskedLoading();
      } else {
        showFullPageLoading();
      }
    },
    [showMaskedLoading, showFullPageLoading]
  );

  const hideLoading = useCallback(() => {
    hideAllLoading();
  }, [hideAllLoading]);

  return (
    <LoadingContext.Provider
      value={{
        loadingState,
        showFullPageLoading,
        hideFullPageLoading,
        showMaskedLoading,
        hideMaskedLoading,
        showPartialLoading,
        hidePartialLoading,
        hideAllLoading,
        // Legacy support
        showLoading,
        hideLoading,
        // Legacy properties for backward compatibility
        isLoading: loadingState.fullPage || loadingState.masked,
        overlayOpacity: loadingState.masked ? 0.4 : undefined,
      }}
    >
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoadingContext = () => useContext(LoadingContext);
